import { Container, Graphics, Sprite, TilingSprite, Texture } from 'pixi.js';
import { assetLoader } from '../core/AssetLoader';

// Depth layer constants
export const DEPTH_LAYERS = {
  DEEP_SPACE: -100,      // bg_layer_1 - deep space background
  BG_NEBULA: -90,        // bg_layer_2 - decorative background nebula (very far back)
  BG_NEBULA_2: -80,      // bg_layer_3 - second nebula layer (closer, more vivid)
  STARFIELD: -45,        // Existing Starfield.ts system (-45 to -30)
  GAME_NEBULA: -10,      // Server-spawned interactive nebulae
  PLANETS: -5,           // Planets with subtle glow
  GAMEPLAY: 0,           // Ships, asteroids, projectiles
  FOREGROUND_DUST: 10,   // Speed sensation particles
  UI: 100,               // HUD elements
} as const;

// Cache for dust texture
let dustTextureGenerated = false;
let dustTexture: Texture | null = null;

export class DepthLayerManager {
  // Layer 1: Deep space background
  private deepSpaceBg: TilingSprite | null = null;

  // Layer 2: Nebula background
  private nebulaBg: TilingSprite | null = null;

  // Layer 3: Second nebula
  private nebulaBg2: TilingSprite | null = null;

  // Background container (fixed to screen space, not world space)
  private bgContainer: Container;

  // Foreground dust particles (speed effect) - in world space
  private foregroundDustContainer!: Container;
  private dustParticles: { sprite: Sprite; vx: number; vy: number; life: number }[] = [];
  private readonly MAX_DUST_PARTICLES = 50;

  // Camera tracking for dust particles
  private lastCameraX: number = 0;
  private lastCameraY: number = 0;

  // Canvas dimensions
  private canvasWidth: number;
  private canvasHeight: number;

  // Background textures (loaded externally)
  private bgLayer1Texture: Texture | null = null;
  private bgLayer2Texture: Texture | null = null;
  private bgLayer3Texture: Texture | null = null;

  // World container (for dust particles)
  private worldContainer: Container;

  // Resize handler
  private resizeHandler: () => void;

  constructor(
    worldContainer: Container,
    canvasWidth: number,
    canvasHeight: number,
    bgLayer1Texture?: Texture,
    bgLayer2Texture?: Texture,
    bgLayer3Texture?: Texture
  ) {
    this.worldContainer = worldContainer;
    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;
    this.bgLayer1Texture = bgLayer1Texture || null;
    this.bgLayer2Texture = bgLayer2Texture || null;
    this.bgLayer3Texture = bgLayer3Texture || null;

    // Create background container (fixed screen space - will be added to scene root)
    this.bgContainer = new Container();
    this.bgContainer.sortableChildren = true;

    // Create foreground dust (in world space)
    this.createForegroundDustLayer();

    // Create background layers (in screen space via bgContainer)
    this.createBackgroundLayers();

    // Setup resize handler
    this.resizeHandler = () => this.handleResize();
    window.addEventListener('resize', this.resizeHandler);
  }

  /**
   * Get the background container - should be added to scene root (not worldContainer)
   */
  getBgContainer(): Container {
    return this.bgContainer;
  }

  private createBackgroundLayers(): void {
    // Layer 1: Deep space — tile scale large enough that one tile fills most of the screen
    if (this.bgLayer1Texture) {
      this.deepSpaceBg = new TilingSprite({
        texture: this.bgLayer1Texture,
        width: this.canvasWidth,
        height: this.canvasHeight,
      });
      this.deepSpaceBg.position.set(0, 0);
      this.deepSpaceBg.zIndex = DEPTH_LAYERS.DEEP_SPACE;
      this.deepSpaceBg.tileScale.set(1.5, 1.5);
      this.bgContainer.addChild(this.deepSpaceBg);
    } else {
      this.createFallbackBackground();
    }

    // Layer 2: Nebula — large scale to hide repeat seams
    if (this.bgLayer2Texture) {
      this.nebulaBg = new TilingSprite({
        texture: this.bgLayer2Texture,
        width: this.canvasWidth,
        height: this.canvasHeight,
      });
      this.nebulaBg.position.set(0, 0);
      this.nebulaBg.zIndex = DEPTH_LAYERS.BG_NEBULA;
      this.nebulaBg.alpha = 0.6;
      this.nebulaBg.tileScale.set(2.0, 2.0);
      this.bgContainer.addChild(this.nebulaBg);
    }

    // Layer 3: Second nebula — even larger scale
    if (this.bgLayer3Texture) {
      this.nebulaBg2 = new TilingSprite({
        texture: this.bgLayer3Texture,
        width: this.canvasWidth,
        height: this.canvasHeight,
      });
      this.nebulaBg2.position.set(0, 0);
      this.nebulaBg2.zIndex = DEPTH_LAYERS.BG_NEBULA_2;
      this.nebulaBg2.alpha = 0.4;
      this.nebulaBg2.tileScale.set(2.5, 2.5);
      this.bgContainer.addChild(this.nebulaBg2);
    }
  }

  private createFallbackBackground(): void {
    // Fallback: create a simple colored rectangle if texture fails to load
    const fallback = new Graphics();
    fallback.rect(0, 0, this.canvasWidth, this.canvasHeight);
    fallback.fill({ color: 0x0a1628, alpha: 1 });
    fallback.zIndex = DEPTH_LAYERS.DEEP_SPACE;
    this.bgContainer.addChild(fallback);
  }

  private createForegroundDustLayer(): void {
    // Generate dust texture if not exists
    if (!dustTextureGenerated) {
      const dustGfx = new Graphics();
      dustGfx.circle(3, 3, 2);
      dustGfx.fill({ color: 0xf5e8d1, alpha: 0.6 });
      dustGfx.circle(3, 3, 3);
      dustGfx.fill({ color: 0xf5e8d1, alpha: 0.2 });
      dustTexture = assetLoader.generateTexture(dustGfx, 'depth-dust');
      dustGfx.destroy();
      dustTextureGenerated = true;
    }

    this.foregroundDustContainer = new Container();
    this.foregroundDustContainer.zIndex = DEPTH_LAYERS.FOREGROUND_DUST;
    this.worldContainer.addChild(this.foregroundDustContainer);
  }

  private handleResize(): void {
    const canvasWidth = window.innerWidth;
    const canvasHeight = window.innerHeight;

    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;

    if (this.deepSpaceBg) {
      this.deepSpaceBg.width = canvasWidth;
      this.deepSpaceBg.height = canvasHeight;
    }

    if (this.nebulaBg) {
      this.nebulaBg.width = canvasWidth;
      this.nebulaBg.height = canvasHeight;
    }

    if (this.nebulaBg2) {
      this.nebulaBg2.width = canvasWidth;
      this.nebulaBg2.height = canvasHeight;
    }
  }

  /**
   * Update parallax layers based on camera position
   */
  update(cameraX: number, cameraY: number, playerSpeed: number = 0, cameraZoom: number = 1): void {
    // ===== BACKGROUND LAYERS =====
    // Parallax via tilePosition - backgrounds stay fixed to screen, but tile offset creates depth
    if (this.deepSpaceBg) {
      // Layer 1: Very slow parallax (most distant)
      this.deepSpaceBg.tilePosition.x = -cameraX * 0.02;
      this.deepSpaceBg.tilePosition.y = -cameraY * 0.02;
    }

    if (this.nebulaBg) {
      // Layer 2: Medium parallax
      this.nebulaBg.tilePosition.x = -cameraX * 0.08;
      this.nebulaBg.tilePosition.y = -cameraY * 0.08;

      // Fade nebula when zoomed out to reduce visible tiling
      const ZOOM_FADE_START = 0.5;
      const ZOOM_MIN = 0.125;
      const zoomFactor = Math.max(0, Math.min(1, (cameraZoom - ZOOM_MIN) / (ZOOM_FADE_START - ZOOM_MIN)));
      this.nebulaBg.alpha = 0.2 + 0.4 * zoomFactor;
    }

    if (this.nebulaBg2) {
      // Layer 3: Faster parallax (closer)
      this.nebulaBg2.tilePosition.x = -cameraX * 0.12;
      this.nebulaBg2.tilePosition.y = -cameraY * 0.12;

      // Fade when zoomed out
      const ZOOM_FADE_START = 0.5;
      const ZOOM_MIN = 0.125;
      const zoomFactor = Math.max(0, Math.min(1, (cameraZoom - ZOOM_MIN) / (ZOOM_FADE_START - ZOOM_MIN)));
      this.nebulaBg2.alpha = 0.15 + 0.25 * zoomFactor;
    }

    // ===== FOREGROUND DUST =====
    const deltaCameraX = cameraX - this.lastCameraX;
    const deltaCameraY = cameraY - this.lastCameraY;
    this.lastCameraX = cameraX;
    this.lastCameraY = cameraY;

    this.foregroundDustContainer.position.set(cameraX, cameraY);
    this.updateForegroundDust(deltaCameraX, deltaCameraY, playerSpeed);
  }

  private updateForegroundDust(deltaCameraX: number, deltaCameraY: number, playerSpeed: number): void {
    // Spawn particles based on speed
    const spawnRate = Math.min(playerSpeed / 150, 1) * 0.4;
    if (Math.random() < spawnRate && this.dustParticles.length < this.MAX_DUST_PARTICLES && dustTexture) {
      this.spawnDustParticle(deltaCameraX, deltaCameraY);
    }

    // Update existing particles
    const FOREGROUND_SCROLL_FACTOR = 1.3;
    for (let i = this.dustParticles.length - 1; i >= 0; i--) {
      const particle = this.dustParticles[i];

      // Move particle faster than camera for "rushing past" effect
      particle.sprite.x += particle.vx - deltaCameraX * (FOREGROUND_SCROLL_FACTOR - 1);
      particle.sprite.y += particle.vy - deltaCameraY * (FOREGROUND_SCROLL_FACTOR - 1);

      // Fade out
      particle.life -= 0.02;
      particle.sprite.alpha = Math.max(0, particle.life * 0.5);

      // Remove dead particles
      if (particle.life <= 0) {
        particle.sprite.destroy();
        this.dustParticles.splice(i, 1);
      }
    }
  }

  private spawnDustParticle(deltaCameraX: number, deltaCameraY: number): void {
    if (!dustTexture) return;

    const x = (Math.random() - 0.5) * this.canvasWidth * 1.2;
    const y = (Math.random() - 0.5) * this.canvasHeight * 1.2;

    const sprite = new Sprite(dustTexture);
    sprite.anchor.set(0.5);
    sprite.position.set(x, y);
    sprite.scale.set(0.5 + Math.random() * 1.0);
    sprite.alpha = 0.3 + Math.random() * 0.2;

    this.foregroundDustContainer.addChild(sprite);

    const speed = 1 + Math.random() * 2;
    const moveAngle = Math.atan2(-deltaCameraY, -deltaCameraX);

    this.dustParticles.push({
      sprite,
      vx: Math.cos(moveAngle) * speed,
      vy: Math.sin(moveAngle) * speed,
      life: 0.8 + Math.random() * 0.4,
    });
  }

  /**
   * Handle viewport resize (legacy method for external calls)
   */
  resize(width: number, height: number): void {
    this.canvasWidth = width;
    this.canvasHeight = height;
    this.handleResize();
  }

  getGameplayDepth(): number {
    return DEPTH_LAYERS.GAMEPLAY;
  }

  getPlanetDepth(): number {
    return DEPTH_LAYERS.PLANETS;
  }

  destroy(): void {
    window.removeEventListener('resize', this.resizeHandler);

    this.deepSpaceBg?.destroy();
    this.nebulaBg?.destroy();
    this.nebulaBg2?.destroy();
    this.bgContainer?.destroy();

    for (const particle of this.dustParticles) {
      particle.sprite.destroy();
    }
    this.dustParticles = [];
    this.foregroundDustContainer?.destroy();
  }
}
